import { useCallback, useMemo, useState } from "react"
import { useIntl } from "react-intl"

import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"
import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { reassignBooking, reassignConversation } from "api"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { useRequestDecision } from "lib/hooks/useRequestDecision"
import { ReassignRequestConversation } from "reducers/conversations/conversations.reducer"

import { BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ConfirmDialog, {
  ConfirmDialogProps
} from "components/v2/conversationList/components/ConfirmDialog/ConfirmDialog"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

interface MyRequestsListProps {
  conversations: ReassignRequestConversation[]
}

interface MyRequestsListItem extends ConversationListItem<ReassignRequestConversation> {}

type DialogType = "DECISION" | "CANCEL"

const MyRequestsList = ({ conversations }: MyRequestsListProps) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const user = useAppSelector(state => state.auth.user)
  const { handleRequestDecision } = useRequestDecision()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState<DialogType>("DECISION")
  const [selectedConversation, setSelectedConversation] = useState<ReassignRequestConversation>()
  const dialogProps: Record<DialogType, Omit<ConfirmDialogProps, "isOpen">> = {
    DECISION: {
      title: intl.formatMessage({ id: "requests.take_over.header" }),
      message: intl.formatMessage({ id: "requests.take_over.description" }),
      conversation: selectedConversation,
      onConfirm: () => {
        if (selectedConversation) {
          handleRequestDecision(selectedConversation, true, true)
          setDialogOpen(false)
        }
      },
      onClose: () => setDialogOpen(false),
      confirmText: intl.formatMessage({ id: "requests.take_over.accept" }),
      cancelText: intl.formatMessage({ id: "requests.take_over.decline" }),
      onCancel: () => {
        if (selectedConversation) {
          handleRequestDecision(selectedConversation, false, false)
          setDialogOpen(false)
        }
      }
    },
    CANCEL: {
      title: intl.formatMessage({ id: "requests.cancel.header" }),
      message: intl.formatMessage({ id: "requests.cancel.description" }),
      conversation: selectedConversation,
      onConfirm: () => {
        const bookingId = selectedConversation?.metadata?.bookingId
        if (selectedConversation?.state === "booked" && bookingId) {
          dispatch(reassignBooking({ booking_ids: [bookingId], staff_id: user?.id! }))
        } else {
          dispatch(
            reassignConversation({
              conversation: selectedConversation as AssignedConversation,
              assignedId: user?.id!
            })
          )
        }
        setDialogOpen(false)
      },
      onClose: () => setDialogOpen(false),
      confirmText: intl.formatMessage({ id: "requests.cancel.approve" })
    }
  }
  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "status",
      name: intl.formatMessage({ id: "label.overview.status" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.errand_started" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: ReassignRequestConversation): MyRequestsListItem => {
      const isChildConversation = conv.profile?.type === "child"
      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          status:
            conv.reassignTo === user?.id
              ? intl.formatMessage({ id: "label.requests.status.waiting_for_you" })
              : intl.formatMessage({ id: "label.requests.status.waiting_for_colleague" }),
          start: <DateTimePillCell value={conv.created} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {}),
          status: conv.reassignTo === user?.id ? "waiting_for_you" : "waiting_for_colleague",
          start: sortValueFunctions.created(conv)
        }
      }
    },
    [intl, user?.id]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)),
    [conversations, mapConversationListItem]
  )

  const openDelegationDialog = (item: MyRequestsListItem) => {
    setSelectedConversation(item.conversation)
    if (item.conversation.reassignTo === user?.id) {
      setDialogType("DECISION")
    } else {
      setDialogType("CANCEL")
    }
    setDialogOpen(true)
  }

  const shouldNotifyItem = (item: MyRequestsListItem) => {
    return item.conversation.reassignTo === user?.id
  }

  return (
    <>
      <ConversationTable
        title={intl.formatMessage({ id: "label.overview.requests" })}
        columns={columns}
        items={list}
        emptyMessage={intl.formatMessage({ id: "label.overview.requests_empty" })}
        onItemClick={openDelegationDialog}
        shouldNotify={shouldNotifyItem}
      />
      <ConfirmDialog isOpen={dialogOpen} {...dialogProps[dialogType]} />
    </>
  )
}

export default MyRequestsList
